import React, { useState, useEffect } from 'react';
import { analyzeCorrelations } from '../api';
import VisualizationDisplay from './VisualizationDisplay';
import { InfoTooltip, ErrorState } from './shared/UIComponents';
import LoadingSpinner from './LoadingSpinner';

function CorrelationAnalysis({ datasetName }) {
  const [selectedDataset, setSelectedDataset] = useState(datasetName || '');
  const [savePlot, setSavePlot] = useState(true);
  const [correlationData, setCorrelationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update selectedDataset when datasetName prop changes
  useEffect(() => {
    if (datasetName) {
      setSelectedDataset(datasetName);
    }
  }, [datasetName]);

  const handleAnalyzeCorrelations = async () => {
    if (!selectedDataset) {
      setError('Please select a dataset to analyze correlations.');
      return;
    }
    setLoading(true);
    setError(null);
    setCorrelationData(null);
    try {
      const result = await analyzeCorrelations(selectedDataset);
      setCorrelationData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="correlation-analysis">
      <div className="correlation-analysis__container">
        <InfoTooltip 
          title="Correlation Analysis" 
          content="Analyze relationships between features in your dataset. Generates a correlation matrix and heatmap to identify strong positive and negative correlations."
        >
          <h2 className="correlation-analysis__title">Correlation Analysis</h2>
        </InfoTooltip>
        <div className="correlation-analysis__controls">
          <label className="correlation-analysis__label">Dataset: </label>
          <input 
            type="text" 
            value={selectedDataset} 
            onChange={e => setSelectedDataset(e.target.value)} 
            placeholder="Enter dataset name"
            className="correlation-analysis__input"
          />
          <label className="correlation-analysis__checkbox-label">
            <input 
              type="checkbox" 
              checked={savePlot} 
              onChange={e => setSavePlot(e.target.checked)}
              className="correlation-analysis__checkbox"
            />
            Save Plot
          </label>
          <button 
            onClick={handleAnalyzeCorrelations} 
            disabled={loading || !selectedDataset} 
            className="correlation-analysis__analyze-btn"
          >
            {loading ? 'Analyzing...' : 'Analyze Correlations'}
          </button>
        </div>
        
        {loading && (
          <div className="correlation-analysis__loading">
            <LoadingSpinner text="Generating correlation analysis and heatmap..." />
          </div>
        )}
        
        {error && (
          <ErrorState 
            message={error}
            onRetry={() => {
              setError(null);
              handleAnalyzeCorrelations();
            }}
          />
        )}
        
        {correlationData && (
          <div className="correlation-analysis__results">
            <h3 className="correlation-analysis__results-title">Correlation Analysis Results</h3>
            <pre className="correlation-analysis__results-json">
              {JSON.stringify(correlationData, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default CorrelationAnalysis;
